import { useCallback, useEffect, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useChainManager } from './useChainManager';
import { ADDRESS_ZERO } from '../../utils/contractUtils';

/**
 * Unified hook for token approval across all chains (BNB, Solana, etc.)
 * Replaces the old useApproveTokenListener that only worked with EVM chains
 */
export function useApproveToken(
  tokenAddress?: string,
  spender?: string,
  onApproved?: () => void,
) {
  const { chainManager, wallet, currentChain } = useChainManager();
  const [isApproved, setIsApproved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const toast = useToast({
    duration: 2000,
    isClosable: true,
    position: 'bottom',
  });

  // Check approval status
  const checkApproval = useCallback(async () => {
    if (!tokenAddress || !spender || !wallet?.address) {
      setIsApproved(false);
      return;
    }

    // Native token doesn't need approval
    if (tokenAddress === ADDRESS_ZERO || tokenAddress === '') {
      setIsApproved(true);
      return;
    }

    try {
      setIsChecking(true);
      const adapter = chainManager.getCurrentAdapter();

      // Check if adapter supports unified approval check
      if (!adapter.checkTokenApproval) {
        console.warn(
          'Adapter does not support checkTokenApproval, skipping check',
        );
        setIsChecking(false);
        return;
      }

      const result = await adapter.checkTokenApproval({
        tokenAddress,
        spender,
        owner: wallet.address,
      });

      setIsApproved(result.approved);
    } catch (error) {
      console.error('Failed to check token approval:', error);
      setIsApproved(false);
    } finally {
      setIsChecking(false);
    }
  }, [tokenAddress, spender, wallet?.address, chainManager]);

  // Approve token
  const approve = useCallback(async () => {
    if (!tokenAddress || !spender || !wallet?.address) {
      toast({
        title: 'Error',
        description: 'Token address and spender are required',
        status: 'error',
      });
      return;
    }

    // Native token doesn't need approval
    if (tokenAddress === ADDRESS_ZERO || tokenAddress === '') {
      setIsApproved(true);
      return;
    }

    try {
      setIsLoading(true);
      const adapter = chainManager.getCurrentAdapter();

      // Check if adapter supports unified approval
      if (!adapter.approveToken) {
        throw new Error(
          `Token approval not supported on ${currentChain} chain`,
        );
      }

      const result = await adapter.approveToken({
        tokenAddress,
        spender,
        // amountWei is optional - undefined means max approval
      });

      if (!result.success) {
        throw new Error(result.error || 'Approval failed');
      }

      // Wait a bit for the transaction to be mined/indexed
      // The event listener will update isApproved automatically
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Failed to approve token:', error);
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to approve token',
        status: 'error',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [
    tokenAddress,
    spender,
    wallet?.address,
    chainManager,
    currentChain,
    toast,
  ]);

  // Subscribe to approval events
  useEffect(() => {
    if (!tokenAddress || !spender || !wallet?.address) {
      return;
    }

    // Native token doesn't need approval events
    if (tokenAddress === ADDRESS_ZERO || tokenAddress === '') {
      return;
    }

    const adapter = chainManager.getCurrentAdapter();

    // Check if adapter supports approval event subscription
    if (!adapter.onTokenApproval) {
      // Fallback: poll for approval status
      const interval = setInterval(() => {
        checkApproval();
      }, 5000); // Check every 5 seconds

      return () => clearInterval(interval);
    }

    // Subscribe to approval events
    const unsubscribe = adapter.onTokenApproval(
      tokenAddress,
      spender,
      eventData => {
        // Verify this is for the current wallet
        if (
          wallet?.address &&
          eventData.owner?.toLowerCase() === wallet.address.toLowerCase() &&
          eventData.spender?.toLowerCase() === spender.toLowerCase()
        ) {
          setIsApproved(true);
          onApproved?.();
        }
      },
    );

    return () => {
      unsubscribe();
    };
  }, [
    tokenAddress,
    spender,
    wallet?.address,
    chainManager,
    onApproved,
    checkApproval,
  ]);

  // Check approval status on mount and when dependencies change
  useEffect(() => {
    checkApproval();
  }, [checkApproval]);

  // Recheck when chain changes
  useEffect(() => {
    checkApproval();
  }, [currentChain, checkApproval]);

  return {
    isApproved,
    isLoading,
    isChecking,
    approve,
    checkApproval,
  };
}
